import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";

interface QuestionSummary {
    id: number;
    displayText: string;
    status: "answered" | "unanswered";
}

interface ExamInfo {
    title: string;
    description: string;
    duration: string;
}

interface CandidateInfo {
    id: string;
    full_name: string;
    email: string;
    mugshot_url?: string | null;
}

export default function CandidateSummaryPage() {
    const { testId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [examInfo, setExamInfo] = useState<ExamInfo | null>(null);
    const [summaries, setSummaries] = useState<QuestionSummary[]>([]);
    const [answeredCount, setAnsweredCount] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [loading, setLoading] = useState(true);
    const [candidate, setCandidate] = useState<CandidateInfo | null>(null);

    useEffect(() => {
        async function loadSummary() {
            setLoading(true);
            try {
                // 🧩 Get logged-in user
                const {
                    data: { user },
                    error: userError,
                } = await supabase.auth.getUser();
                if (userError || !user) {
                    navigate("/candidate/login");
                    return;
                }

                // 🧠 Lookup candidate profile
                const { data: candidateData } = await supabase
                    .from("candidates")
                    .select("id, full_name, email, mugshot_url")
                    .or(`email.eq.${user.email},phone.eq.${user.phone}`)
                    .single();

                if (candidateData) setCandidate(candidateData);

                // 🧭 Load exam info
                const { data: test } = await supabase
                    .from("tests")
                    .select("title, description, duration_minutes")
                    .eq("id", Number(testId))
                    .single();

                if (test)
                    setExamInfo({
                        title: test.title,
                        description: test.description,
                        duration: `${test.duration_minutes} min`,
                    });

                // 🧩 Load all questions
                const { data: questions } = await supabase
                    .from("questions")
                    .select("id")
                    .eq("test_id", Number(testId))
                    .order("id");

                // 💾 Load all answers for this candidate
                const { data: attempts } = await supabase
                    .from("exam_attempts")
                    .select("question_id")
                    .eq("test_id", Number(testId))
                    .eq("candidate_id", candidateData?.id ?? "");

                const answeredIds = new Set(attempts?.map((a) => a.question_id));

                // ✅ Build summaries safely typed
                const list: QuestionSummary[] = (questions ?? []).map((q: any, i: number) => ({
                    id: q.id,
                    displayText: (i + 1).toString(),
                    status: (answeredIds.has(q.id) ? "answered" : "unanswered") as
                        | "answered"
                        | "unanswered",
                }));

                setSummaries(list);
                setTotalQuestions(list.length);
                setAnsweredCount(list.filter((x) => x.status === "answered").length);
                setAnsweredCount(list.filter((x) => x.status === "answered").length);
            } catch (err) {
                console.error("Error loading summary:", err);
            } finally {
                setLoading(false);
            }
        }

        loadSummary();
        const interval = setInterval(loadSummary, 10000); // auto-refresh every 10s
        return () => clearInterval(interval);
    }, [testId, navigate]);

    const progress =
        totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
    const remaining = totalQuestions - answeredCount;

    if (loading)
        return <p className="text-center py-8">Loading summary...</p>;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50 py-10">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8">

                {/* ✅ Candidate header */}
                {candidate && (
                    <div className="flex flex-col sm:flex-row items-center justify-between mb-6 border-b pb-4">
                        <div className="flex items-center gap-4">
                            {candidate.mugshot_url ? (
                                <img
                                    src={candidate.mugshot_url}
                                    alt="Candidate mugshot"
                                    className="w-16 h-16 rounded-full border border-gray-200 object-cover shadow-sm"
                                />
                            ) : (
                                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                    👤
                                </div>
                            )}
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">
                                    {candidate.full_name}
                                </h2>
                                <p className="text-sm text-gray-500">{candidate.email}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ✅ Test header */}
                <h1 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-500 mb-2">
                    {examInfo?.title || `Test #${testId}`}
                </h1>
                <p className="text-center text-gray-600 mb-1">
                    {examInfo?.description}
                </p>
                <p className="text-center text-sm text-gray-500 mb-8">
                    Duration: {examInfo?.duration}
                </p>

                {/* ✅ Progress bar */}
                <div className="w-full bg-gray-200 h-4 rounded-full overflow-hidden mb-6">
                    <div
                        className="h-4 bg-gradient-to-r from-indigo-500 to-violet-500 transition-all"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                <p className="text-center text-sm text-gray-700 mb-6">
                    {answeredCount} of {totalQuestions} answered ({progress}% complete)
                </p>

                {/* ✅ Question grid */}
                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-3 mb-10">
                    {summaries.map((q) => (
                        <button
                            key={q.id}
                            onClick={() => navigate(`/candidate/test/${testId}?q=${q.id}`)}
                            className={`rounded-xl w-10 h-10 text-white text-sm font-semibold flex items-center justify-center transition ${q.status === "answered"
                                    ? "bg-green-500 hover:bg-green-600"
                                    : "bg-red-400 hover:bg-red-500"
                                }`}
                            title={`Question ${q.displayText}`}
                        >
                            {q.displayText}
                        </button>
                    ))}
                </div>

                {/* ✅ Footer controls */}
                <div className="flex justify-center gap-6">
                    <button
                        onClick={() => {
                            const firstUnanswered = summaries.find(
                                (s) => s.status === "unanswered"
                            );
                            const resumeId =
                                firstUnanswered?.id || summaries[0]?.id || undefined;
                            if (resumeId)
                                navigate(`/candidate/test/${testId}?q=${resumeId}`);
                        }}
                        className="px-6 py-2.5 bg-gray-200 rounded-full font-medium hover:bg-gray-300 transition"
                    >
                        Resume Exam
                    </button>

                    <button
                        onClick={() => navigate(`/candidate/thankyou?test=${testId}`)}
                        disabled={remaining > 0}
                        className={`px-6 py-2.5 rounded-full font-semibold shadow transition ${remaining > 0
                                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                : "bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:opacity-90"
                            }`}
                    >
                        Submit Exam
                    </button>
                </div>

                {remaining > 0 && (
                    <p className="mt-4 text-center text-sm text-red-500">
                        ⚠️ You still have {remaining} unanswered{" "}
                        {remaining === 1 ? "question" : "questions"}.
                    </p>
                )}

                <p className="mt-6 text-center text-xs text-gray-400 italic">
                    Auto-refresh every 10 seconds
                </p>
            </div>
        </div>
    );
}
